import React, { useEffect, useState } from "react";
import { api } from "../context/AuthContext";
import { useAuth } from "../context/AuthContext";
import { money } from "../lib/format";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Storefront, Ticket, ShoppingCart, FilePdf, Plus, Minus, Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : null);

export function MemberStore({ savingsAccts = [], onPurchased }) {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]); // [{product, qty}]
  const [cartOpen, setCartOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("savings");
  const [acct, setAcct] = useState("");
  const [info, setInfo] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [discount, setDiscount] = useState({ welcome_eligible: false, tier_pct: 0 });
  const { user } = useAuth();
  const cartKey = `coop_store_cart_${user?.id || "anon"}`;

  const load = () => Promise.all([api.get("/me/store/products"), api.get("/me/store/orders"), api.get("/site-config")])
    .then(([p, o, s]) => { setProducts(p.data); setOrders(o.data); setSite(s.data); })
    .catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (hydrated || products.length === 0) return;
    const rebuild = (saved) => (saved || []).map((s) => {
      const p = products.find((x) => x.id === (s.id || s.product_id));
      if (!p || p.stock === 0) return null;
      const qty = p.stock != null ? Math.min(s.qty, p.stock) : s.qty;
      return { product: p, qty };
    }).filter(Boolean);
    const fromLocal = () => { try { return JSON.parse(localStorage.getItem(cartKey) || "[]"); } catch (e) { return []; } };
    api.get("/me/store/cart").then((r) => {
      const saved = (r.data.items && r.data.items.length) ? r.data.items : fromLocal();
      const rebuilt = rebuild(saved);
      if (rebuilt.length) setCart(rebuilt);
      setHydrated(true);
    }).catch(() => {
      const rebuilt = rebuild(fromLocal());
      if (rebuilt.length) setCart(rebuilt);
      setHydrated(true);
    });
    api.get("/me/store/welcome-discount").then((r) => setDiscount(r.data)).catch(() => {});
  }, [products, hydrated, cartKey]);

  useEffect(() => {
    if (!hydrated) return;
    const items = cart.map((c) => ({ product_id: c.product.id, qty: c.qty }));
    try { localStorage.setItem(cartKey, JSON.stringify(items.map((i) => ({ id: i.product_id, qty: i.qty })))); } catch (e) { /* ignore */ }
    const tmr = setTimeout(() => { api.put("/me/store/cart", { items }).catch(() => {}); }, 600);
    return () => clearTimeout(tmr);
  }, [cart, hydrated, cartKey]);

  const afterBuy = () => { load(); onPurchased && onPurchased(); };

  const cartCurrency = cart.length ? cart[0].product.currency : null;
  const cartCount = cart.reduce((n, c) => n + c.qty, 0);
  const cartTotal = cart.reduce((n, c) => n + c.product.price * c.qty, 0);
  const cashAvail = !!(site && (site.cashapp_tag || site.cashapp_link));
  const cashUsable = cashAvail && cartCurrency === "USD";
  const savingsOptions = savingsAccts.filter((s) => s.currency === cartCurrency);
  const wVal = discount.welcome_eligible ? Math.min(discount.welcome_type === "fixed" ? Number(discount.welcome_amount || 0) : cartTotal * (Number(discount.welcome_pct || 0) / 100), cartTotal) : 0;
  const tVal = cartTotal * (Number(discount.tier_pct || 0) / 100);
  const discVal = Math.max(wVal, tVal);
  const discIsTier = tVal >= wVal && tVal > 0;

  const addToCart = (p) => {
    if (p.stock === 0) return;
    setCart((prev) => {
      if (prev.length && prev[0].product.currency !== p.currency) {
        toast.error(`El carrito solo permite productos en ${prev[0].product.currency}. Vacíalo para cambiar de moneda.`);
        return prev;
      }
      const found = prev.find((c) => c.product.id === p.id);
      if (found) {
        if (p.stock !== null && p.stock !== undefined && found.qty >= p.stock) { toast.error("No hay más stock disponible"); return prev; }
        return prev.map((c) => (c.product.id === p.id ? { ...c, qty: c.qty + 1 } : c));
      }
      return [...prev, { product: p, qty: 1 }];
    });
    toast.success(`${p.name} agregado al carrito`);
  };
  const setQty = (id, delta) => {
    setCart((prev) => prev.flatMap((c) => {
      if (c.product.id !== id) return [c];
      const max = c.product.stock;
      let q = c.qty + delta;
      if (max !== null && max !== undefined && q > max) { toast.error("No hay más stock disponible"); q = max; }
      if (q <= 0) return [];
      return [{ ...c, qty: q }];
    }));
  };
  const removeItem = (id) => setCart((prev) => prev.filter((c) => c.product.id !== id));

  const openCart = () => {
    setInfo(null);
    setMethod(savingsAccts.some((s) => s.currency === cartCurrency) ? "savings" : (cashUsable ? "cashapp" : "savings"));
    setAcct(savingsAccts.filter((s) => s.currency === cartCurrency)[0]?.id || "");
    setCartOpen(true);
  };

  const checkout = async () => {
    if (method === "savings" && !acct) { toast.error(`Necesitas una cuenta de ahorro en ${cartCurrency}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/me/store/cart/checkout`, {
        items: cart.map((c) => ({ product_id: c.product.id, qty: c.qty })),
        payment_method: method,
        savings_account_id: method === "savings" ? acct : "",
      });
      if (data.awaiting) { setInfo(data); toast.success("Pedido creado. Realiza el pago por Cash App."); afterBuy(); }
      else {
        if (data.ticket) toast.success(`¡Compra realizada! Ganaste 1 boleto: ${data.ticket.code}`);
        else toast.success("¡Compra realizada!");
        setDiscount({ welcome_eligible: false, tier_pct: 0 });
        setCart([]); setCartOpen(false); afterBuy();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const downloadTicket = async (code) => {
    try {
      const res = await api.get(`/me/raffle/tickets/${code}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `boleto_${code}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el boleto"); }
  };
  const downloadReceipt = async (id) => {
    try {
      const res = await api.get(`/me/store/orders/${id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `recibo_${id.slice(0, 8)}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el recibo"); }
  };

  return (
    <div className="mt-8" data-testid="member-store">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Storefront size={24} weight="duotone" className="text-emerald-700" />
          <h2 className="font-display font-bold text-emerald-900 text-xl">Tienda</h2>
        </div>
        <button
          data-testid="cart-open-btn"
          onClick={() => (cart.length ? openCart() : toast.info("Tu carrito está vacío. Agrega productos."))}
          className="relative inline-flex items-center gap-2 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white px-4 h-11 font-semibold transition-transform hover:-translate-y-0.5 shadow-sm"
        >
          <ShoppingCart size={18} weight="bold" /> Carrito
          {cartCount > 0 && (
            <span data-testid="cart-count-badge" className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[11px] font-bold min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center">{cartCount}</span>
          )}
        </button>
      </div>

      {loading ? (
        <p className="text-slate-500 py-6">Cargando...</p>
      ) : products.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-slate-500" data-testid="store-empty">Aún no hay productos disponibles.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="store-products">
          {products.map((p) => {
            const soldOut = p.stock === 0;
            const inCart = cart.find((c) => c.product.id === p.id);
            return (
              <div key={p.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`store-product-${p.id}`}>
                <div className="h-40 bg-slate-50 flex items-center justify-center overflow-hidden">
                  {imgSrc(p.image_url) ? <img src={imgSrc(p.image_url)} alt={p.name} className="h-full w-full object-cover" /> : <Storefront size={44} weight="duotone" className="text-slate-300" />}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-emerald-900">{p.name}</p>
                    {p.stock !== null && p.stock !== undefined && (
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${p.stock === 0 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`store-stock-${p.id}`}>{p.stock === 0 ? "Agotado" : `Quedan ${p.stock}`}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 line-clamp-2 mt-0.5 min-h-[2rem]">{p.description}</p>
                  <p className="font-mono-num font-bold text-emerald-900 text-lg mt-1">{money(p.price, p.currency)}</p>
                  <Button
                    data-testid={`add-to-cart-${p.id}`}
                    size="sm"
                    disabled={soldOut}
                    onClick={() => addToCart(p)}
                    className="mt-3 w-full bg-emerald-800 hover:bg-emerald-900 gap-1.5 disabled:opacity-50"
                  >
                    <Plus size={15} weight="bold" /> {soldOut ? "Agotado" : inCart ? `En carrito (${inCart.qty}) · Agregar` : "Agregar al carrito"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cart dialog */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent data-testid="cart-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-emerald-900 flex items-center gap-2"><ShoppingCart size={20} weight="duotone" /> Tu carrito</DialogTitle>
            <DialogDescription>{info ? "Instrucciones de pago" : `${cartCount} artículo(s) · ${cartCurrency ? money(cartTotal, cartCurrency) : ""}`}</DialogDescription>
          </DialogHeader>

          {info ? (
            <div className="space-y-3" data-testid="cashapp-instructions">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm">
                <p className="font-semibold text-emerald-900 mb-2">Paga con Cash App</p>
                <p className="text-slate-700">Envía <b>{money(info.price, info.currency)}</b> a:</p>
                {info.cashapp_tag && <p className="font-mono-num text-lg text-emerald-800 mt-1" data-testid="cashapp-tag">{info.cashapp_tag}</p>}
                {info.cashapp_link && <a href={info.cashapp_link} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-emerald-700 underline break-all">{info.cashapp_link}</a>}
                <p className="mt-3 text-slate-700">Incluye esta referencia en la nota del pago:</p>
                <p className="font-mono-num text-lg font-bold text-amber-700" data-testid="cashapp-ref">{info.pay_ref}</p>
                <p className="mt-3 text-xs text-slate-500">Tu pedido quedó <b>pendiente de pago</b>. GEHI lo confirmará al recibir el dinero y entonces se procesará (con tu boleto si aplica).</p>
              </div>
              <DialogFooter><Button data-testid="cashapp-close" onClick={() => { setCart([]); setCartOpen(false); }} className="bg-emerald-800 hover:bg-emerald-900">Entendido</Button></DialogFooter>
            </div>
          ) : cart.length === 0 ? (
            <p className="text-slate-500 py-6 text-center" data-testid="cart-empty">Tu carrito está vacío.</p>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {cart.map((c) => (
                  <div key={c.product.id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2.5" data-testid={`cart-item-${c.product.id}`}>
                    <div className="h-12 w-12 rounded-md bg-white flex items-center justify-center overflow-hidden shrink-0 border border-gray-100">
                      {imgSrc(c.product.image_url) ? <img src={imgSrc(c.product.image_url)} alt={c.product.name} className="h-full w-full object-cover" /> : <Storefront size={20} className="text-slate-300" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-emerald-900 truncate">{c.product.name}</p>
                      <p className="text-xs text-slate-500 font-mono-num">{money(c.product.price, c.product.currency)}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button data-testid={`cart-dec-${c.product.id}`} onClick={() => setQty(c.product.id, -1)} className="h-7 w-7 rounded-md border border-gray-200 hover:bg-white flex items-center justify-center text-emerald-800"><Minus size={13} weight="bold" /></button>
                      <span className="w-6 text-center text-sm font-semibold" data-testid={`cart-qty-${c.product.id}`}>{c.qty}</span>
                      <button data-testid={`cart-inc-${c.product.id}`} onClick={() => setQty(c.product.id, 1)} className="h-7 w-7 rounded-md border border-gray-200 hover:bg-white flex items-center justify-center text-emerald-800"><Plus size={13} weight="bold" /></button>
                      <button data-testid={`cart-remove-${c.product.id}`} onClick={() => removeItem(c.product.id)} className="h-7 w-7 rounded-md hover:bg-red-50 flex items-center justify-center text-red-500 ml-1"><Trash size={14} weight="bold" /></button>
                    </div>
                  </div>
                ))}
              </div>

              {discVal > 0 && (
                <div className="flex items-center justify-between text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="welcome-discount-banner">
                  <span>🎁 {discIsTier ? `Descuento por referidos activos (${discount.tier_pct}%)` : `Descuento de bienvenida${discount.welcome_type === "fixed" ? "" : ` (${discount.welcome_pct}%)`}`}</span>
                  <span className="font-mono-num">- {money(discVal, cartCurrency)}</span>
                </div>
              )}
              <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                <span className="text-sm text-slate-500">{discVal > 0 ? "Total a pagar" : "Total"}</span>
                <span className="font-mono-num font-bold text-emerald-900 text-lg" data-testid="cart-total">{money(cartTotal - discVal, cartCurrency)}</span>
              </div>

              {cashUsable && (
                <div>
                  <Label>Método de pago</Label>
                  <select data-testid="cart-method-select" className="mt-1 h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm" value={method} onChange={(e) => setMethod(e.target.value)}>
                    <option value="savings">Débito de mi cuenta de ahorro</option>
                    <option value="cashapp">Cash App (USD)</option>
                  </select>
                </div>
              )}

              {method === "savings" ? (
                <div>
                  <Label>Pagar desde</Label>
                  <select data-testid="cart-account-select" className="mt-1 h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm" value={acct} onChange={(e) => setAcct(e.target.value)}>
                    {savingsOptions.length === 0 && <option value="">{`Sin cuenta en ${cartCurrency || ""}`}</option>}
                    {savingsOptions.map((s) => (<option key={s.id} value={s.id}>{`${s.account_number} · ${money(s.balance, s.currency)}`}</option>))}
                  </select>
                  {savingsOptions.length === 0 && (
                    <p className="text-xs text-amber-700 mt-1.5">No tienes una cuenta de ahorro en {cartCurrency}. {cashUsable ? "Puedes pagar con Cash App." : "Contacta a GEHI para abrir una."}</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2.5">Pagarás con Cash App a {site?.cashapp_tag || "la cuenta"} de GEHI. Al continuar recibirás las instrucciones y una referencia.</p>
              )}

              <p className="text-xs flex items-start gap-1.5 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5"><Ticket size={16} weight="duotone" className="shrink-0 mt-0.5" /> Las compras de 100 o más te otorgan 1 boleto para el sorteo semanal.</p>

              <DialogFooter className="gap-2">
                <Button data-testid="cart-clear" variant="outline" onClick={() => setCart([])} className="border-gray-200 gap-1.5"><X size={15} /> Vaciar</Button>
                <Button data-testid="cart-checkout-btn" onClick={checkout} disabled={busy || (method === "savings" && !acct)} className="bg-emerald-800 hover:bg-emerald-900">
                  {busy ? "Procesando..." : method === "cashapp" ? "Continuar" : `Pagar todo · ${money(cartTotal - discVal, cartCurrency)}`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {orders.length > 0 && (
        <div className="mt-8" data-testid="store-orders">
          <h3 className="font-display font-bold text-emerald-900 mb-3">Mis compras</h3>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-gray-100"><th className="px-4 py-3 font-medium">Fecha</th><th className="px-4 py-3 font-medium">Producto</th><th className="px-4 py-3 font-medium text-right">Monto</th><th className="px-4 py-3 font-medium">Boleto</th><th className="px-4 py-3 font-medium">Recibo</th></tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-gray-50 last:border-0" data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-3 text-slate-600">{(o.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-emerald-900 font-semibold">
                      {o.product_name}
                      {o.status === "awaiting_payment" && <span className="ml-2 text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Pago pendiente</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-num">{money(o.price, o.currency)}</td>
                    <td className="px-4 py-3">{o.ticket ? <button data-testid={`download-ticket-${o.id}`} onClick={() => downloadTicket(o.ticket.code)} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full hover:bg-amber-200" title="Descargar certificado del boleto"><FilePdf size={12} weight="bold" /> {o.ticket.code}</button> : <span className="text-slate-400 text-xs">—</span>}</td>
                    <td className="px-4 py-3">{o.status === "awaiting_payment" ? <span className="text-slate-400 text-xs">—</span> : <button data-testid={`download-receipt-${o.id}`} onClick={() => downloadReceipt(o.id)} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full hover:bg-emerald-200" title="Descargar recibo de compra"><FilePdf size={12} weight="bold" /> Recibo</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
